import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

// Enrichit un <select> avec une dropdown filtrable (recherche sur le texte des
// <option>, déjà localisé côté serveur). Générique : pays, langue, etc.
//
// Deux usages :
// - directement sur le <select> (data-controller="tom-select") ;
// - sur un conteneur (ligne de réglage) avec un <select data-tom-select-target="select">
//   et data-action="click->tom-select#open" pour rendre toute la ligne cliquable.
//
// Le placeholder est optionnel : data-tom-select-placeholder-value="…".
export default class extends Controller {
  static values = { placeholder: String }
  static targets = ["select"]

  connect() {
    const element = this.hasSelectTarget ? this.selectTarget : this.element
    // tom-select relaie l'événement `change` sur le <select> d'origine,
    // ce qui préserve le submit automatique du formulaire profil.
    this.select = new TomSelect(element, {
      maxOptions: null,
      hideSelected: false,
      // Recherche insensible aux accents (« Bresil » trouve « Brésil »).
      diacritics: true,
      searchField: ["text"],
      placeholder: this.placeholderValue || undefined,
      // La dropdown est rattachée au <body> pour échapper au `overflow-hidden`
      // des cards (sinon elle est clippée par le conteneur parent).
      dropdownParent: "body"
    })

    // tom-select ne recale la dropdown que sur le scroll/resize de `window`
    // (cf. sources 2.6.2). Le layout dashboard scrolle dans un conteneur
    // interne (`lg:overflow-auto`), dont le scroll ne remonte pas jusqu'à
    // `window` : on écoute en phase de capture sur `document`, qui voit
    // passer le scroll (non-bubbling) de n'importe quel ancêtre (#2464).
    this.repositionDropdown = (event) => {
      // Le scroll interne de la liste d'options (`.ts-dropdown-content`,
      // ~200 pays) ne déplace pas le champ : rien à recaler.
      if (this.select.dropdown.contains(event.target)) return

      // Le champ est sorti du conteneur scrollé (clippé par son
      // `overflow-auto`) : la dropdown flotterait au-dessus du reste de la
      // page (rattachée au <body>, sans clipping propre). On la ferme plutôt
      // que de la laisser détachée de tout champ visible.
      const container = event.target
      if (container instanceof Element) {
        const controlRect = this.select.control.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()
        if (controlRect.bottom < containerRect.top || controlRect.top > containerRect.bottom) {
          this.select.close()
          return
        }
      }

      this.select.positionDropdown()
    }
    this.select.on("dropdown_open", () => {
      document.addEventListener("scroll", this.repositionDropdown, { capture: true, passive: true })
    })
    this.select.on("dropdown_close", () => {
      document.removeEventListener("scroll", this.repositionDropdown, true)
    })
  }

  disconnect() {
    document.removeEventListener("scroll", this.repositionDropdown, true)

    if (this.select) {
      this.select.destroy()
      this.select = null
    }
  }

  // Ouvre la dropdown depuis un clic n'importe où sur la ligne (openOnFocus par
  // défaut). Ignore les clics déjà dans le contrôle pour ne pas re-déclencher.
  open(event) {
    if (!this.select || this.select.control.contains(event.target)) return

    this.select.focus()
  }

  // Soumet le formulaire parent au changement de sélection — remplace l'ancien
  // `onchange="this.closest('form').requestSubmit()"` inline (bloqué par la CSP
  // script-src sans `unsafe-inline`, #2113). Brancher via
  // `data-action="change->tom-select#submitForm"` sur le `<select>`.
  submitForm(event) {
    event.target.closest("form")?.requestSubmit()
  }
}
